import {
  AdSlotArticleFooter2,
  AdSlotArticleHead,
  AdSlotArticleInline1,
  AdSlotArticleSkyscraper1,
  AdSlotArticleSkyscraper2,
} from "src/components/Ads/AdSlots";
import React, { FC, useContext, useEffect } from "react";
import parse, { attributesToProps, domToReact } from "html-react-parser";

import AdBanner from "src/components/Ads/AdBanner";
import AdContext from "src/components/Ads/AdContext";
import ArticleCategory from "./ArticleCategory";
import ArticleHighlights from "./ArticleHighlights";
import ArticleRecommendation from "./ArticleRecommendation";
import ButtonLinkApp from "../../Navigation/ButtonLinkApp";
import ButtonLinkExternal from "../../Navigation/ButtonLinkExternal";
import Head from "next/head";
import LazyLoad from "../../../lib/LazyLoad";
import LocaleLink from "../../Navigation/LocaleLink";
import NewsletterBlogSnippet from "../../Newsletter/NewsletterBlogSnippet";
import ProductHightlights from "../Product/ProductHightlights";
import SocialShareButtons from "../../Social/SocialShareButtons";
import { TheCycleverseTheme } from "src/theme";
import TrustindexSnippet from "src/components/Social/TrustindexSnippet";
import { default as categoriesConfig } from "../../../categoriesArticles.json";
import environment from "../../../environment";
import { makeStyles } from "@material-ui/core/styles";
import { default as productCategories } from "../../../categoriesExplodedTree.json";
import useTranslation from "next-translate/useTranslation";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  root: {
    textAlign: "center",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.globalMarginDesktop,
      marginRight: theme.globalMarginDesktop,
    },
    [theme.breakpoints.down("xs")]: {
      marginLeft: theme.globalMarginMobile,
      marginRight: theme.globalMarginMobile,
    },
  },
  container: {
    [theme.breakpoints.up("md")]: {
      flex: "1 1 auto",
    },
    width: "100%",
    maxWidth: theme.widths.contentWidth,
    display: "inline-block",
    textAlign: "left",
    "& a[id]": {
      // fix anchor scroll position
      display: "block",
      position: "relative",
      top: -theme.searchBarHeight - theme.spacing(1),
      visibility: "hidden",
    },
  },
  title: {
    marginTop: 0,
    fontWeight: 900,
    fontSize: 32,
    lineHeight: "1.1",
  },
  image: {
    width: "100%",
    height: "auto",
    aspectRatio: "attr(width) / attr(height)",
  },
  short: {
    marginBottom: 20,
    fontSize: 21,
  },
  content: {
    ...theme.formText,
    marginBottom: theme.spacing(4),
  },
  author: {
    fontSize: "small",
    marginBottom: theme.spacing(2),
  },
  authorPrefix: {
    whiteSpace: "pre-wrap",
  },
  authorName: {
    fontWeight: 600,
  },
  copyright: {
    fontSize: "small",
    marginBottom: theme.spacing(2),
  },
  socialMediaShare: {
    marginBottom: theme.spacing(4),
  },
  adHead: {
    margin: "auto",
    marginBottom: theme.spacing(4),
  },
  containerBox: {
    [theme.breakpoints.up("md")]: {
      display: "flex",
      justifyContent: "center",
    },
  },
  adSkyscraperLane: {
    [theme.breakpoints.up("md")]: {
      flex: "0 0 300px",
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  adSkyscraper1: {
    display: "inline-block",
    position: "absolute",
    top: 800,
  },
  adSkyscraper2: {
    display: "inline-block",
    top: 100,
    position: "sticky",
    marginTop: 2000,
  },
}));

const parseOptions = (isInOverflowContainer: boolean): any => {
  return {
    replace: (domNode) => {
      if (domNode.type == "tag" && domNode.name == "p" && domNode.children.length == 1) {
        const pContent = domNode.children[0].data;
        if (
          pContent &&
          (pContent.length <= 0 ||
            pContent == " " ||
            pContent == "" ||
            pContent == "&nbsp;" ||
            pContent.charCodeAt(0).toString(16) == "a0")
        ) {
          return <br />;
        }
      }

      if (domNode.type == "tag" && domNode.name == "table" && domNode.attribs.style) {
        const isTableWithFixedSize = domNode.attribs.style.indexOf("width: 100%;") < 0;
        const { attribs, children } = domNode;
        const props = attributesToProps(attribs ? attribs : {});

        if (isTableWithFixedSize) {
          return (
            <div className="FX-Article-TableWrapper-custom-width">
              <table {...props}>{domToReact(children, parseOptions(isInOverflowContainer))}</table>
            </div>
          );
        } else {
          return (
            <div style={{ overflowX: "auto" }}>
              <table {...props}>{domToReact(children, parseOptions(isInOverflowContainer))}</table>
            </div>
          );
        }
      }

      if (domNode.type == "tag" && domNode.name == "img") {
        return (
          // NOTE(kjellski): Needs discussion, since this doesn't come with an aspect ration,
          //                 no chance of always doing it right unfortunately.
          // <img loading="lazy"
          //   src={domNode.attribs.src}
          //   width={domNode.width || 600}
          //   height={domNode.height || 400}
          //   alt={domNode.attribs.alt}
          // />
          <LazyLoad offset={200} placeholder={<span>{domNode.attribs.alt}</span>} overflow={isInOverflowContainer} once>
            <img loading="lazy" src={domNode.attribs.src} alt={domNode.attribs.alt} />
          </LazyLoad>
        );
      }

      if (domNode.type == "tag" && domNode.name == "iframe") {
        return (
          <LazyLoad offset={200} height={domNode.attribs.height} overflow={isInOverflowContainer} once>
            <iframe src={domNode.attribs.src} width={domNode.attribs.width} height={domNode.attribs.height} />
          </LazyLoad>
        );
      }

      if (
        domNode.attribs &&
        domNode.attribs.id === "products" &&
        domNode.children &&
        domNode.children.length > 0 &&
        domNode.children[0].data
      ) {
        const idString = domNode.children[0].data;
        const productIds = idString.split(",").map((item) => item.trim());
        return (
          <LazyLoad offset={100} height={200} overflow={isInOverflowContainer} once>
            <ProductHightlights recommendation={{ type: "ids", ids: productIds }} />
          </LazyLoad>
        );
      }

      if (domNode.attribs && domNode.attribs.component === "ProductHighlights") {
        const type = domNode.attribs.type;
        if (type == "topHitsByBrand") {
          const categoryUrlId = domNode.attribs.category;
          if (productCategories[categoryUrlId]) {
            const category = productCategories[categoryUrlId].path
              .split("|")
              .map((p) => p.split(":")[0])
              .join(" > ");
            const price = domNode.attribs.price;
            const brands = domNode.attribs.brands;
            const sort = domNode.attribs.sort;
            return (
              <LazyLoad offset={100} height={200} overflow={isInOverflowContainer} once>
                <ProductHightlights
                  recommendation={{
                    type: type,
                    brands: brands ? brands.split(",") : [],
                    priceMin: price ? parseInt(price.split(",")[0]) : environment.products.minimumPrice,
                    priceMax: price ? parseInt(price.split(",")[1]) : environment.products.maximumPrice,
                    category: category,
                    sort: sort ? sort : "default",
                  }}
                />
              </LazyLoad>
            );
          }
        } else if (type == "search") {
          const search = domNode.attribs.search;
          const categories = domNode.attribs.categories;
          const price = domNode.attribs.price;
          const brands = domNode.attribs.brands;
          const sort = domNode.attribs.sort;
          return (
            <LazyLoad offset={100} height={200} overflow={isInOverflowContainer} once>
              <ProductHightlights
                recommendation={{
                  type: type,
                  searchString: search || "",
                  brands: brands ? brands.split(",") : [],
                  priceMin: price ? parseInt(price.split(",")[0]) : environment.products.minimumPrice,
                  priceMax: price ? parseInt(price.split(",")[1]) : environment.products.maximumPrice,
                  categories: categories ? categories.split(",") : [],
                  sort: sort ? sort : "default",
                }}
              />
            </LazyLoad>
          );
        }
      }

      if (domNode.attribs && domNode.attribs.component === "ArticleHighlights") {
        return <ArticleHighlights highlightsClass="FX-Article-Highlights-Blog" articlesIds={domNode.attribs.ids} />;
      }

      if (domNode.attribs && domNode.attribs.component === "ButtonLinkApp") {
        const ios = domNode.attribs.ios;
        const android = domNode.attribs.android;
        return <ButtonLinkApp ios={ios} android={android} />;
      }

      if (domNode.attribs && domNode.attribs.component === "ButtonLinkExternal") {
        const href = domNode.attribs?.href;
        const data = domNode.children && domNode.children.length >= 1 ? domNode.children[0].data : null;
        return <ButtonLinkExternal href={href}>{data}</ButtonLinkExternal>;
      }

      if (domNode.attribs && domNode.attribs.component === "NewsletterBlogSnippet") {
        const { children } = domNode;
        return (
          <NewsletterBlogSnippet>{domToReact(children, parseOptions(isInOverflowContainer))}</NewsletterBlogSnippet>
        );
      }

      if (domNode.attribs && domNode.attribs.component === "TrustindexSnippet") {
        return (
          <LazyLoad offset={100} height={200} overflow={isInOverflowContainer} once>
            <TrustindexSnippet id={domNode.attribs.id} />
          </LazyLoad>
        );
      }
    },
  };
};

const Article: FC<{
  article: any;
  showArticleFooter?: boolean;
  isInOverflowContainer?: boolean;
}> = ({ article, showArticleFooter = true, isInOverflowContainer = false }) => {
  const classes = useStyles();
  const { t, lang } = useTranslation("common");
  const locale = lang;
  const { setFooterAd } = useContext(AdContext);

  useEffect(() => {
    setFooterAd(<AdBanner slot={AdSlotArticleFooter2} hideOnMobile={true} />);
  }, [setFooterAd, article]);

  if (!article) {
    // eslint-disable-next-line no-console
    console.warn("Undefined article in Article.js");
    return null;
  }

  return (
    <>
      <Head>
        <link rel="preload" href={article.images[0].uri} as="image" />
      </Head>
      <div className={classes.root} data-test={`page-blog-${article.id}`}>
        <AdBanner slot={AdSlotArticleHead} className={classes.adHead} />
        <div className={classes.containerBox}>
          <div className={classes.adSkyscraperLane}>
            <AdBanner slot={AdSlotArticleSkyscraper1} hideOnMobile className={classes.adSkyscraper1} />
            <AdBanner slot={AdSlotArticleSkyscraper2} hideOnMobile className={classes.adSkyscraper2} />
          </div>
          <div className={classes.container}>
            <LocaleLink notStyled href={`/blog/[generic]`} as={`/blog/${article.category}`}>
              <ArticleCategory category={article.category} />
            </LocaleLink>
            <h1 className={classes.title}>{article.title}</h1>
            <img
              loading="lazy"
              src={article.images[0].uri}
              width={600}
              height={415}
              alt={article.images[0].alt ? article.images[0].alt : article.title}
              className={classes.image}
            />

            <div className={classes.author}>
              <span className={classes.authorPrefix}>{t("articleAuthorPrefix")}</span>
              <span className={classes.authorName}>{article.author}</span>
            </div>
            <div>
              {t("articleUpdatedAt", {
                date: new Date(article.updatedAt).toLocaleDateString(locale === "de" ? "de-DE" : "en-US"),
              })}
            </div>
            <div className={classes.copyright}>{article.copyright}</div>
            <div className={classes.short}>{article.short}</div>
            <div className={classes.content}>{parse(article.content, parseOptions(isInOverflowContainer))}</div>
            {showArticleFooter && (
              <>
                <LazyLoad overflow={isInOverflowContainer}>
                  <SocialShareButtons
                    className={classes.socialMediaShare}
                    title={article.title}
                    mediaUrl={article.images[0].uri}
                  />
                </LazyLoad>
                {categoriesConfig.categoriesInApps.indexOf(article.category) < 0 && (
                  <ArticleRecommendation articleId={article.id} />
                )}
              </>
            )}
          </div>
          <div className={classes.adSkyscraperLane}></div>
        </div>
        <AdBanner slot={AdSlotArticleInline1} className={classes.adHead} />
      </div>
    </>
  );
};

export default Article;
